import React, { useState, useEffect } from 'react';
import { Switch, Box, Typography, TextField, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import { updateMemberTicketAutoDeduction } from '../../../../api/ticket';
import { errorMessages } from '../../../../utils/methods';

// ============================================
// Styled Components
// ============================================
const ModalContent = styled(Box)({
  padding: '28px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

const ToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const ToggleTextGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const ToggleTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#262626',
});

const ToggleCaption = styled(Typography)({
  fontSize: '13px',
  color: '#8C8C8C',
});

const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const FormLabel = styled(Typography)({
  fontSize: '15px',
  fontWeight: 600,
  color: '#262626',
  width: '92px',
  flexShrink: 0,
});

const InputGroup = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
});

const StyledTextField = styled(TextField)({
  width: '80px',
  '& .MuiOutlinedInput-root': {
    height: '40px',
    borderRadius: '6px',
    '& fieldset': {
      borderColor: '#d9d9d9',
    },
    '&:hover fieldset': {
      borderColor: '#4AB3BC',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0F7F85',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#262626',
    padding: '8px 12px',
    textAlign: 'center',
  },
});

const UnitText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#666',
  whiteSpace: 'nowrap',
});

const PreviewText = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#0F7F85',
  marginLeft: 'auto',
});

const NoticeBox = styled(Box)({
  background: '#F7F8F9',
  borderRadius: '6px',
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const NoticeText = styled(Typography)({
  fontSize: '12.5px',
  color: '#8C8C8C',
  lineHeight: 1.5,
});

const ButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  padding: '4px 32px 32px',
});

// 오늘 이후 최초로 도래하는 앵커일 (말일 보정 포함) — 백엔드 계산과 동일 규칙
const nextAnchorDate = anchorDay => {
  const today = dayjs().startOf('day');
  const clamp = base => {
    const day = Math.min(anchorDay, base.daysInMonth());
    return base.date(day);
  };
  const sameMonth = clamp(today.startOf('month'));
  return sameMonth.isAfter(today) ? sameMonth : clamp(today.add(1, 'month').startOf('month'));
};

// 자동 차감을 화면에 보일지 — 기업회원 자동 차감 설정(로그인 응답 autoDeductionYn)이 켜진 곳만, SUPERADMIN 은 항상.
// 상품 폼(TicketEditStep01)과 같은 기준. 회원 상세의 카드 줄·상품 정보 수정 탭·중지 모달 안내가 함께 따른다.
export const useAutoDeductionVisible = () => {
  const loginUser = useSelector(state => state.user.user);
  return loginUser?.userRole === 'ROLE_SUPERADMIN' || loginUser?.autoDeductionYn === 'Y';
};

// ============================================
// Component
// ============================================
// 상품 정보 수정 모달의 "자동 차감" 탭 본문. 닫기·새로고침은 부모(onDone)가 맡는다.
// 해제(autoDeductionCount=null)한 회원권도 이 탭에서 다시 켤 수 있다.
const AutoDeductionSettingModal = ({ bigUser, ticket, user, onDone }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [form, setForm] = useState({
    enabled: false,
    count: '',
    anchorDay: '',
  });

  useEffect(() => {
    if (ticket) {
      setForm({
        enabled: ticket.autoDeductionCount != null,
        count: ticket.autoDeductionCount ?? '',
        anchorDay: ticket.autoDeductionAnchorDay ?? '',
      });
    }
  }, [ticket]);

  const saveMutation = useMutation({
    mutationFn: data =>
      updateMemberTicketAutoDeduction(user?.memberId, ticket.memberCourseTicketId, data, bigUser.token),
    onSuccess: () => {
      toast.success(t('자동 차감 설정이 저장되었습니다'));
      onDone?.();
    },
    onError: error => {
      toast.error(errorMessages(error));
    },
  });

  const handleNumberChange = (field, value, max) => {
    const cleaned = value.replace(/[^0-9]/g, '').slice(0, String(max).length);
    setForm(prev => ({ ...prev, [field]: cleaned }));
  };

  const anchorDayNum = Number(form.anchorDay);
  const anchorChanged = anchorDayNum !== ticket?.autoDeductionAnchorDay;
  const previewDate =
    form.enabled && anchorDayNum >= 1 && anchorDayNum <= 31
      ? anchorChanged || !ticket?.nextAutoDeductionDate
        ? nextAnchorDate(anchorDayNum).format('YYYY.MM.DD')
        : dayjs(ticket.nextAutoDeductionDate).format('YYYY.MM.DD')
      : null;

  const handleSave = () => {
    if (!form.enabled) {
      saveMutation.mutate({ autoDeductionCount: null });
      return;
    }
    const count = Number(form.count);
    if (!count || count < 1) {
      toast.error('자동 차감 횟수를 1 이상 입력해주세요.');
      return;
    }
    if (!anchorDayNum || anchorDayNum < 1 || anchorDayNum > 31) {
      toast.error('차감일은 1~31 사이로 입력해주세요.');
      return;
    }
    saveMutation.mutate({
      autoDeductionCount: count,
      autoDeductionAnchorDay: anchorDayNum,
    });
  };

  if (!ticket) return null;

  return (
    <>
        <ModalContent>
          {/* 사용 토글 */}
          <ToggleRow>
            <ToggleTextGroup>
              <ToggleTitle>{t('자동 차감 사용')}</ToggleTitle>
              <ToggleCaption>
                {form.enabled
                  ? '매월 실사용이 설정 횟수에 미달하면 부족분만큼 차감합니다.'
                  : '해제하면 이 회원권의 자동 차감이 중단됩니다.'}
              </ToggleCaption>
            </ToggleTextGroup>
            <Switch
              checked={form.enabled}
              onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
            />
          </ToggleRow>

          {form.enabled && (
            <>
              {/* 차감 횟수 */}
              <FormRow>
                <FormLabel>{t('차감 횟수')}</FormLabel>
                <InputGroup>
                  <UnitText>매월</UnitText>
                  <StyledTextField
                    type="text"
                    value={form.count}
                    onChange={e => handleNumberChange('count', e.target.value, 999)}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                  <UnitText>{t('회 차감')}</UnitText>
                </InputGroup>
              </FormRow>

              {/* 차감일 */}
              <FormRow>
                <FormLabel>{t('차감일')}</FormLabel>
                <InputGroup>
                  <UnitText>매월</UnitText>
                  <StyledTextField
                    type="text"
                    value={form.anchorDay}
                    onChange={e => handleNumberChange('anchorDay', e.target.value, 31)}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                  <UnitText>{t('일에 차감')}</UnitText>
                  {previewDate && (
                    <Tooltip title="다음 차감 예정일" arrow placement="top">
                      <PreviewText>다음 {previewDate}</PreviewText>
                    </Tooltip>
                  )}
                </InputGroup>
              </FormRow>

            </>
          )}

          <NoticeBox>
            <NoticeText>· 변경 사항은 이 회원권에만 적용됩니다.</NoticeText>
            {form.enabled && (
              <NoticeText>· 차감일을 변경하면 이번 주기는 오늘부터 다시 시작됩니다.</NoticeText>
            )}
          </NoticeBox>
        </ModalContent>

        <ButtonWrapper>
          <CommonModalButton variant="cancel" onClick={onDone}>
            {t('취소')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave} disabled={saveMutation.isPending}>
            {t('저장')}
          </CommonModalButton>
        </ButtonWrapper>
    </>
  );
};

export default AutoDeductionSettingModal;
